/**
 * THE MONTHLY STEERING PACK AS A PRODUCED FILE — the portfolio's own document,
 * drawn with the same pen the charter and the requirements document use so the
 * things a project sends out read as one product.
 *
 * WHAT IT IS SHAPED LIKE. A branded A4 pack, decisions first. Page 1 is the
 * executive summary — a verdict, portfolio health as a stacked distribution, the
 * decisions this committee is asked to make, the budget position as a burn bar,
 * and a one-line-per-project table. Every page after is one project's steering
 * detail: a numbered teal band with its status, a verdict, the split RAG, the
 * milestones, its budget, what it is waiting on, the decision it needs, and what
 * moved this month.
 *
 * SAME PEN, SAME CONSTANTS. The visual tokens (the teal band, the ink, the quiet
 * label grey, the hairline rule, the sunk fill), the text wrapper, the logo
 * embed, the deterministic date stamp and the page-n-of-m footer are the change
 * request's and the requirements document's, verbatim — so the pack cannot drift
 * from the house style. What is new is the considered graphics a steering pack
 * needs: a distribution bar, a budget burn bar, and RAG as solid squares, all
 * drawn with rectangles and hairlines. The RAG palette is deliberately SEPARATE
 * from the brand teal — a print-calm green/amber/red, never the accent.
 *
 * TAKES WHAT IT PRINTS. The caller composes the pack (composePortfolioPack) into
 * one frozen PackModel and hands it here with the date. This lays it out and
 * reaches for nothing, so the same pack always produces the same bytes — which is
 * what a document filed in the minutes has to be.
 */
import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import { winAnsi, unwrap } from './pdf-text';
import { drawBand, BRAND } from './pdf-chrome';
import { embedTitleFont } from './pdf-fonts';
import { fmtMoneyShort } from './portfolio-pack';
import { RISK_CAP } from './charter';
import type { PackModel, PackBudget, HealthCount, PackRag, GlanceRow, PackMilestone, PackSignal, PackRisk, PackClosure, PackLessonForward, PackDeliveryEvent } from './portfolio-pack';

/** Money is drawn the one way across the pack — a short figure in the active
 *  currency ("$1.2M", "$402k"), the same formatter the composer uses. */
const money = (n: number): string => fmtMoneyShort(n) || String(Math.round(n));

/** A4 in points, which is what pdf-lib measures in. */
const PAGE = { width: 595.28, height: 841.89 };
const MARGIN = 52;
const WIDTH = PAGE.width - MARGIN * 2;
const FOOT = 38;

const INK = rgb(0.1, 0.13, 0.16);
const QUIET = rgb(0.42, 0.46, 0.5);
const RULE = rgb(0.8, 0.83, 0.85);
/** FlightDeck's own teal, the light palette's --brand. A printed page is light. */
const BAND = rgb(0.043, 0.431, 0.467);
const ON_BAND = rgb(1, 1, 1);
const SUNK = rgb(0.965, 0.972, 0.976);
/** A hairline between table rows, lighter than the RULE used for borders. */
const FAINT = rgb(0.933, 0.945, 0.953);

/* Semantic RAG — deliberately separate from the teal brand accent, print-calm
   (deep, never neon), the same values the approved mock uses. */
const GREEN = rgb(0.122, 0.478, 0.302);
const AMBER = rgb(0.604, 0.416, 0.071);
const RED = rgb(0.678, 0.2, 0.2);
// ORANGE for a High-band risk (the app's --p-high), sitting between RED (critical) and
// AMBER (medium) on the severity ramp the register and drill share.
const ORANGE = rgb(0.584, 0.286, 0.118);
const HOLD = rgb(0.541, 0.58, 0.612);
/** A raised-but-unpaid burn segment — a lighter teal, so it reads as teal-family
   money rather than a fourth RAG colour. */
const TEAL_LIGHT = rgb(0.353, 0.631, 0.659);

const RAG_COLOUR: Record<PackRag, ReturnType<typeof rgb>> = {
  green: GREEN,
  amber: AMBER,
  red: RED,
  hold: HOLD,
  grey: RULE,
};
/** The word's own ink — a status word is coloured, a grey/hold one stays quiet. */
const RAG_INK: Record<PackRag, ReturnType<typeof rgb>> = {
  green: GREEN,
  amber: AMBER,
  red: RED,
  hold: QUIET,
  grey: QUIET,
};

import { MONTHS } from './when';

/** The customer's mark, passed in as bytes so this file reaches for nothing. */
export interface Logo {
  bytes: ArrayBuffer | Uint8Array;
  type: 'png' | 'jpg';
}

export interface PackFacts {
  /** The moment the pack was produced, in ms. Stamped in, never read from the
   *  clock here, so the same pack twice is the same bytes and a test can say what
   *  the date will be. Drives the masthead "Produced" line and the PDF dates. */
  preparedOn: number;
  /** Who produced it — the signed-in person, named in the masthead just before the
   *  produced date. Stamped in at produce time, like the date. */
  preparedBy?: string;
  workspaceName: string;
  /** An optional customer mark, drawn in the masthead in place of the brand square. */
  logo?: Logo;
}

/** "20 Sep 2026" — the produced date, long form for the masthead. */
const asLongDate = (at: number): string => {
  const d = new Date(at);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
/** "20/09/2026" — day first, the same stamp the other documents carry. */
const asDate = (at: number): string => {
  const d = new Date(at);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()}`;
};

/**
 * Words that fit, measured in the font they will be drawn in — the change
 * request's own wrapper, verbatim. A word longer than the whole measure overhangs
 * rather than being dropped.
 */
function wrap(text: string, font: PDFFont, size: number, measure: number): string[] {
  const lines: string[] = [];
  for (const paragraph of unwrap(winAnsi(text)).split('\n')) {
    let line = '';
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const tried = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(tried, size) <= measure) line = tried;
      else {
        if (line) lines.push(line);
        line = word;
      }
    }
    lines.push(line);
  }
  return lines;
}

/** What pdf-lib hands back from embedPng/embedJpg. */
type Art = Parameters<PDFPage['drawImage']>[0] & { width: number; height: number };
interface Embedded {
  art: Art;
  width: number;
  height: number;
}

/** A logo that will not embed is skipped, not fatal — the pack is the point. */
async function embed(pdf: PDFDocument, logo?: Logo): Promise<Embedded | undefined> {
  if (!logo) return undefined;
  try {
    const art =
      logo.type === 'png' ? await pdf.embedPng(logo.bytes) : await pdf.embedJpg(logo.bytes);
    return { art, width: art.width, height: art.height };
  } catch {
    return undefined;
  }
}

/** The same pack produces the same bytes — pdf-lib stamps the save moment into
 *  every file, so the date is taken from the pack's own prepared date. Verbatim. */
function stampDates(pdf: PDFDocument, preparedOn: string): void {
  const [day, month, year] = preparedOn.split('/').map(Number);
  const when =
    Number.isFinite(day) && Number.isFinite(month) && Number.isFinite(year)
      ? new Date(Date.UTC(year, month - 1, day))
      : new Date(Date.UTC(2000, 0, 1));
  pdf.setCreationDate(when);
  pdf.setModificationDate(when);
}

/** Every page carries the board line and page n of m beneath a hairline, so a
 *  loose page still says which pack it came from — the change request's rule, verbatim. */
function stampFooters(pdf: PDFDocument, said: string, font: PDFFont): void {
  const pages = pdf.getPages();
  pages.forEach((page, at) => {
    const size = 7.5;
    page.drawLine({
      start: { x: MARGIN, y: MARGIN + 22 },
      end: { x: MARGIN + WIDTH, y: MARGIN + 22 },
      thickness: 0.75,
      color: RULE,
    });
    page.drawText(winAnsi(said), { x: MARGIN, y: MARGIN + 10, size, font, color: QUIET });
    const numbered = winAnsi(`Page ${at + 1} of ${pages.length}`);
    page.drawText(numbered, {
      x: MARGIN + WIDTH - font.widthOfTextAtSize(numbered, size),
      y: MARGIN + 10,
      size,
      font,
      color: QUIET,
    });
  });
}

/** What the masthead draws, the same on every page. */
interface Masthead {
  workspaceName: string;
  producedOn: string;
  /** Who produced the pack — the signed-in person, named in the masthead just
   *  before the produced date. */
  producedBy?: string;
  logo?: Embedded;
}

/**
 * The pen — knows where it is on the page, starts a new one when it runs out, and
 * draws every section the pack needs. The masthead is redrawn at the top of every
 * page (including one an overflow forces), so a loose page is still branded.
 */
function makePen(pdf: PDFDocument, regular: PDFFont, bold: PDFFont, mast: Masthead, titleFont?: PDFFont) {
  let page: PDFPage = pdf.addPage([PAGE.width, PAGE.height]);
  let y = 0;
  let section = 0;

  /** Draw the masthead at the top of the current page and drop the cursor below it. */
  const masthead = () => {
    const top = PAGE.height - MARGIN;
    const size = 20;
    if (mast.logo) {
      const wide = (mast.logo.width / mast.logo.height) * size;
      const scale = Math.min(1, (WIDTH / 3) / wide);
      page.drawImage(mast.logo.art, { x: MARGIN, y: top - size * scale, width: wide * scale, height: size * scale });
    } else {
      // The FlightDeck brand mark — a teal square with a white F.
      page.drawRectangle({ x: MARGIN, y: top - size, width: size, height: size, color: BAND });
      page.drawText('F', { x: MARGIN + 6, y: top - size + 5.5, size: 12, font: bold, color: ON_BAND });
    }
    page.drawText('FlightDeck', { x: MARGIN + size + 8, y: top - size + 6, size: 12.5, font: bold, color: INK });

    const meta1 = winAnsi(`${mast.workspaceName} · Portfolio`);
    const who = (mast.producedBy ?? '').trim();
    const meta2 = winAnsi(who ? `Produced by ${who} · ${mast.producedOn}` : `Produced ${mast.producedOn}`);
    const m = 9;
    page.drawText(meta1, { x: MARGIN + WIDTH - regular.widthOfTextAtSize(meta1, m), y: top - 6, size: m, font: regular, color: QUIET });
    page.drawText(meta2, { x: MARGIN + WIDTH - regular.widthOfTextAtSize(meta2, m), y: top - 6 - m * 1.55, size: m, font: regular, color: QUIET });

    y = top - size - 20;
  };

  const room = (needed: number) => {
    if (y - needed >= MARGIN + FOOT) return;
    page = pdf.addPage([PAGE.width, PAGE.height]);
    masthead();
  };

  masthead(); // the first page

  const put = (x: number, baseline: number, s: string, size: number, font: PDFFont, color: ReturnType<typeof rgb>) =>
    page.drawText(winAnsi(s), { x, y: baseline, size, font, color });
  const measure = (s: string, font: PDFFont, size: number) => font.widthOfTextAtSize(winAnsi(s), size);

  /** Cut a string to fit `max`, adding an ellipsis — for a no-wrap table cell. */
  const fit = (s: string, font: PDFFont, size: number, max: number): string => {
    const clean = winAnsi(s);
    if (font.widthOfTextAtSize(clean, size) <= max) return clean;
    let lo = 0;
    let hi = clean.length;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (font.widthOfTextAtSize(`${clean.slice(0, mid)}…`, size) <= max) lo = mid;
      else hi = mid - 1;
    }
    return `${clean.slice(0, lo)}…`;
  };

  /** A solid RAG square, its top-left corner a touch below the text baseline. */
  const square = (x: number, baseline: number, s: number, colour: ReturnType<typeof rgb>) =>
    page.drawRectangle({ x, y: baseline - 1, width: s, height: s, color: colour });

  /** A slip tone → its print-calm RAG ink. */
  const TONE: Record<'g' | 'a' | 'r', ReturnType<typeof rgb>> = { g: GREEN, a: AMBER, r: RED };

  /** A milestone diamond — a zero-duration point, so a diamond, never a bar. Filled
   *  in its slip colour; a go-live is ringed (a larger outline behind), the one date
   *  each project is judged on. Centred on `cy`, ~9pt across. */
  const diamond = (x: number, cy: number, tone: 'g' | 'a' | 'r', ringed: boolean) => {
    const col = TONE[tone];
    // pdf-lib draws an SVG path with its (0,0) at (x, y) and y growing downward, so
    // a path of height H centred vertically wants y = cy + H/2.
    if (ringed) page.drawSvgPath('M6 0 L12 6 L6 12 L0 6 Z', { x: x - 1, y: cy + 7, borderColor: col, borderWidth: 1 });
    page.drawSvgPath('M5 0 L10 5 L5 10 L0 5 Z', { x, y: cy + 5, color: col });
  };

  /** The Slip cell — a rising NE arrow (drawn as vectors, like the glance trend) when
   *  a milestone has slipped, then the reading ("+7d" / "on plan" / "on time") in its
   *  tone. The arrow always points up-and-right, so the direction reads whatever the
   *  colour. */
  const slipCell = (x: number, baseline: number, text: string, tone: 'g' | 'a' | 'r', arrow: boolean) => {
    const col = TONE[tone];
    let tx = x;
    if (arrow) {
      const ln = (x1: number, y1: number, x2: number, y2: number) =>
        page.drawLine({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, thickness: 1.2, color: col });
      ln(x, baseline, x + 7, baseline + 7);
      ln(x + 7, baseline + 7, x + 3, baseline + 7);
      ln(x + 7, baseline + 7, x + 7, baseline + 3);
      tx = x + 11;
    }
    put(tx, baseline, text, 9, arrow ? bold : regular, col);
  };

  /** THE MILESTONES TABLE — planned → forecast → actual with a slip, shared by a
   *  project's own detail page and the portfolio-level "landing this cycle" table
   *  (the rows differ, the drawing does not). A diamond carries state; the go-live
   *  is ringed. Draws its own header and a note when there is nothing to show. */
  const drawMilestones = (rows: PackMilestone[], emptyNote: string) => {
    const cols = [0.4, 0.15, 0.15, 0.15, 0.15].map((w) => w * WIDTH);
    const xs: number[] = [];
    let acc = MARGIN;
    for (const c of cols) {
      xs.push(acc);
      acc += c;
    }
    y -= 12;
    ['Milestone', 'Planned', 'Forecast', 'Actual', 'Slip'].forEach((h, i) => put(xs[i], y, h.toUpperCase(), 7.5, bold, QUIET));
    y -= 5;
    page.drawLine({ start: { x: MARGIN, y }, end: { x: MARGIN + WIDTH, y }, thickness: 1.2, color: RULE });
    if (rows.length === 0) {
      y -= 15;
      put(MARGIN, y, emptyNote, 9.5, regular, QUIET);
      return;
    }
    for (const r of rows) {
      // A row with a cadence confidence line beneath its forecast stands taller, so
      // the "50/50" note has room without crowding the next milestone.
      const rowH = r.confidence ? 24 : 16;
      room(rowH);
      y -= rowH;
      const base = y + (r.confidence ? 12 : 4);
      diamond(xs[0], base + 1, r.diamondTone, r.golive);
      put(xs[0] + 16, base, fit(r.name, regular, 9, cols[0] - 16 - 4), 9, regular, INK);
      put(xs[1], base, r.planned, 9, regular, INK);
      put(xs[2], base, r.forecast, 9, regular, r.forecast === '—' ? QUIET : INK);
      put(xs[3], base, r.actual, 9, regular, r.actual === '—' ? QUIET : INK);
      slipCell(xs[4], base, r.slip, r.slipTone, r.slipArrow);
      if (r.confidence) put(xs[2], base - 10, fit(r.confidence, regular, 7, cols[2] - 4), 7, regular, QUIET);
      page.drawLine({ start: { x: MARGIN, y }, end: { x: MARGIN + WIDTH, y }, thickness: 0.5, color: FAINT });
    }
  };

  const api = {
    get pages() {
      return pdf.getPageCount();
    },
    down: (by: number) => {
      y -= by;
    },
    room,

    /** Start the next intended page (a project detail) with its masthead drawn. */
    newPage() {
      page = pdf.addPage([PAGE.width, PAGE.height]);
      masthead();
    },

    /** The document-type eyebrow — teal, small, letter-spaced caps. */
    eyebrow(text: string) {
      y -= 20;
      put(MARGIN, y, text.toUpperCase(), 9, bold, BAND);
      y -= 5;
    },

    title(text: string) {
      y -= 20;
      put(MARGIN, y, text, 20, titleFont ?? bold, INK);
    },

    sub(text: string) {
      y -= 14;
      put(MARGIN, y, text, 10, regular, QUIET);
    },

    /** The teal title-rule under the page-1 title. */
    titleRule() {
      y -= 9;
      page.drawRectangle({ x: MARGIN, y, width: WIDTH, height: 2, color: BAND });
    },

    /** A flowing paragraph, INK unless told otherwise. */
    para(body: string, opts: { size?: number; colour?: ReturnType<typeof rgb>; gap?: number } = {}) {
      const size = opts.size ?? 10.5;
      const leading = size * 1.5;
      for (const line of wrap(body, regular, size, WIDTH)) {
        room(leading);
        y -= leading;
        put(MARGIN, y, line, size, regular, opts.colour ?? INK);
      }
      if (opts.gap) y -= opts.gap;
    },

    /** A quiet, uppercase structural label over a hairline — the charter's h2. */
    label(text: string) {
      room(30);
      y -= 17;
      put(MARGIN, y, text.toUpperCase(), 8.5, bold, QUIET);
      y -= 5;
      page.drawLine({ start: { x: MARGIN, y }, end: { x: MARGIN + WIDTH, y }, thickness: 0.75, color: RULE });
      y -= 8;
    },

    /** The numbered teal band that opens a project's detail page, with its status
     *  chip on the right. The one place the teal band is used, per the design. */
    projectHeader(title: string, word: string) {
      const height = 26;
      room(height + 12);
      y -= height + 4;
      section += 1;
      drawBand(page, { x: MARGIN, yBottom: y, width: WIDTH, title: winAnsi(title), number: section, bold });
      // The status chip — a teal square + the status word, right-aligned on the tint.
      const w = winAnsi(word.toUpperCase());
      const wordW = bold.widthOfTextAtSize(w, 9);
      const textY = y + (height - 9) / 2 + 1;
      const chipX = MARGIN + WIDTH - 12 - wordW;
      page.drawRectangle({ x: chipX - 12, y: textY - 0.5, width: 7, height: 7, color: BRAND });
      put(chipX, textY, word.toUpperCase(), 9, bold, BRAND);
      y -= 10;
    },

    /** PORTFOLIO HEALTH — a stacked distribution bar, then labelled counts. */
    distribution(health: HealthCount[]) {
      const total = health.reduce((n, h) => n + h.n, 0) || 1;
      // The stacked bar.
      const barH = 9;
      y -= barH + 3;
      let x = MARGIN;
      for (const h of health) {
        if (h.n === 0) continue;
        const w = (h.n / total) * WIDTH;
        page.drawRectangle({ x, y, width: w, height: barH, color: RAG_COLOUR[h.rag] });
        x += w;
      }
      // The counts row — one column each: swatch, big number, label.
      y -= 26;
      const colW = WIDTH / health.length;
      health.forEach((h, i) => {
        const cx = MARGIN + i * colW + (i === 0 ? 0 : 13);
        if (i > 0) {
          page.drawLine({ start: { x: MARGIN + i * colW, y: y + 20 }, end: { x: MARGIN + i * colW, y: y - 2 }, thickness: 0.75, color: RULE });
        }
        square(cx, y + 4, 9, RAG_COLOUR[h.rag]);
        put(cx + 13, y, String(h.n), 17, bold, RAG_INK[h.rag]);
        const numW = bold.widthOfTextAtSize(String(h.n), 17);
        put(cx + 13 + numW + 6, y + 1, h.label, 9.5, regular, QUIET);
      });
      y -= 6;
    },

    /** A numbered committee decision — the index in teal, the ask (project bold),
     *  the consequence beneath in quiet. */
    decision(index: number, project: string, ask: string, consequence: string) {
      room(40);
      y -= 7;
      put(MARGIN, y - 10, String(index), 11, bold, BAND);
      const indent = 16;
      const measureW = WIDTH - indent;
      // The ask, with the project name bold then the rest regular, wrapped together.
      const askLines = wrapMixed(`${project} — ${ask}`, project, bold, regular, 10.5, measureW);
      askLines.forEach((ln) => {
        y -= 10.5 * 1.42;
        let cx = MARGIN + indent;
        for (const seg of ln) {
          put(cx, y, seg.text, 10.5, seg.bold ? bold : regular, INK);
          cx += (seg.bold ? bold : regular).widthOfTextAtSize(seg.text, 10.5);
        }
      });
      for (const ln of wrap(consequence, regular, 9.5, measureW)) {
        y -= 9.5 * 1.4;
        put(MARGIN + indent, y, ln, 9.5, regular, QUIET);
      }
      y -= 6;
      page.drawLine({ start: { x: MARGIN, y }, end: { x: MARGIN + WIDTH, y }, thickness: 0.5, color: FAINT });
    },

    /** BUDGET — the figures, then a burn bar (paid / committed / overrun) with a
     *  baseline tick, then the key. Draws nothing but a note when there is no money. */
    burn(b: PackBudget) {
      // Figures row: Baseline / Forecast / Variance.
      const figs: { k: string; v: string; over?: boolean }[] = [];
      if (b.base != null) figs.push({ k: 'Baseline', v: money(b.base) });
      if (b.forecast != null) figs.push({ k: 'Forecast', v: money(b.forecast) });
      if (b.variance) {
        const d = b.variance.diff;
        figs.push({ k: 'Variance', v: `${d > 0 ? '+' : d < 0 ? '-' : ''}${money(Math.abs(d))}`, over: d > 0 });
      }
      if (figs.length) {
        y -= 10;
        const colW = WIDTH / 3;
        figs.forEach((f, i) => {
          const cx = MARGIN + i * colW;
          put(cx, y, f.k.toUpperCase(), 8, bold, QUIET);
          put(cx, y - 13, f.v, 12, bold, f.over ? RED : INK);
        });
        y -= 22;
      }

      const scale = Math.max(b.base ?? 0, b.forecast ?? 0, b.paid + b.committed);
      const barH = 15;
      if (scale > 0) {
        y -= barH + 4;
        // Track.
        page.drawRectangle({ x: MARGIN, y, width: WIDTH, height: barH, color: SUNK, borderColor: RULE, borderWidth: 0.75 });
        const paidW = (b.paid / scale) * WIDTH;
        const commW = (b.committed / scale) * WIDTH;
        if (paidW > 0) page.drawRectangle({ x: MARGIN, y, width: paidW, height: barH, color: BAND });
        if (commW > 0) page.drawRectangle({ x: MARGIN + paidW, y, width: commW, height: barH, color: TEAL_LIGHT });
        // Overrun sliver, if the forecast runs past the baseline.
        if (b.base != null && b.forecast != null && b.forecast > b.base) {
          const overLeft = (b.base / scale) * WIDTH;
          const overW = ((b.forecast - b.base) / scale) * WIDTH;
          page.drawRectangle({ x: MARGIN + overLeft, y, width: overW, height: barH, color: RED });
        }
        // Baseline tick.
        if (b.base != null) {
          const tx = MARGIN + (b.base / scale) * WIDTH;
          page.drawLine({ start: { x: tx, y: y - 3 }, end: { x: tx, y: y + barH + 3 }, thickness: 1.5, color: INK });
        }
        // Key.
        y -= 14;
        const keys: { c: ReturnType<typeof rgb>; t: string }[] = [
          { c: BAND, t: `Paid ${money(b.paid)}` },
          { c: TEAL_LIGHT, t: `Committed ${money(b.committed)}` },
        ];
        if (b.base != null) keys.push({ c: INK, t: `Baseline ${money(b.base)}` });
        if (b.variance && b.variance.diff > 0) keys.push({ c: RED, t: `Over by ${money(b.variance.diff)}` });
        let kx = MARGIN;
        for (const k of keys) {
          page.drawRectangle({ x: kx, y: y + 0.5, width: 8, height: 8, color: k.c });
          put(kx + 12, y, k.t, 8.5, regular, QUIET);
          kx += 12 + measure(k.t, regular, 8.5) + 15;
        }
        y -= 6;
      } else {
        y -= 14;
        put(MARGIN, y, 'No budget is set on these projects.', 9.5, regular, QUIET);
        y -= 4;
      }
    },

    /** The three sub-signals side by side, each a square + cap + one-line reason. */
    signals(three: PackSignal[]) {
      const colW = WIDTH / three.length;
      const pad = 10;
      const reasonLines = three.map((s) => wrap(s.reason, regular, 9.5, colW - pad * 2));
      const rows = Math.max(1, ...reasonLines.map((l) => l.length));
      const height = 14 + rows * (9.5 * 1.3) + 10;
      room(height + 8);
      y -= height + 4;
      page.drawRectangle({ x: MARGIN, y, width: WIDTH, height, borderColor: RULE, borderWidth: 0.75 });
      three.forEach((s, i) => {
        const cx = MARGIN + i * colW;
        if (i > 0) page.drawLine({ start: { x: cx, y }, end: { x: cx, y: y + height }, thickness: 0.75, color: RULE });
        const topY = y + height - 14;
        square(cx + pad, topY, 8, RAG_COLOUR[s.rag]);
        put(cx + pad + 12, topY, s.cap.toUpperCase(), 8, bold, QUIET);
        reasonLines[i].forEach((ln, n) => {
          put(cx + pad, topY - 12 - n * (9.5 * 1.3), ln, 9.5, regular, INK);
        });
      });
      y -= 2;
    },

    /** PORTFOLIO AT A GLANCE — one row per project, a single RAG square + word,
     *  drawn until the page runs out, then "…and N more". */
    glance(rows: GlanceRow[]) {
      const cols = [0.22, 0.13, 0.15, 0.15, 0.08, 0.14, 0.13].map((w) => w * WIDTH);
      const xs: number[] = [];
      let acc = MARGIN;
      for (const c of cols) {
        xs.push(acc);
        acc += c;
      }
      // The trend cell — a small arrow (improving up/worsening down/flat) drawn as
      // vectors, then the word. Same directions the screen shows (0122), read
      // against last month. Nothing when a project is parked or has no history.
      const trendCell = (x: number, base: number, t: GlanceRow['trend']) => {
        if (!t) return;
        let tx = x;
        if (t.arrow) {
          const col = t.arrow === 'up' ? GREEN : t.arrow === 'down' ? RED : QUIET;
          const ln = (x1: number, y1: number, x2: number, y2: number) =>
            page.drawLine({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, thickness: 1.2, color: col });
          if (t.arrow === 'up') {
            ln(x, base, x + 7, base + 7); ln(x + 7, base + 7, x + 3, base + 7); ln(x + 7, base + 7, x + 7, base + 3);
          } else if (t.arrow === 'down') {
            ln(x, base + 7, x + 7, base); ln(x + 7, base, x + 3, base); ln(x + 7, base, x + 7, base + 4);
          } else {
            ln(x, base + 5, x + 7, base + 5); ln(x, base + 2, x + 7, base + 2);
          }
          tx = x + 11;
        }
        const col = t.arrow === 'up' ? GREEN : t.arrow === 'down' ? RED : QUIET;
        put(tx, base, fit(t.text, regular, 8.5, x + cols[3] - tx - 2), 8.5, regular, col);
      };
      // Header.
      y -= 12;
      const heads = ['Project', 'Owner', 'Health', 'Trend', 'Progress', 'Go-Live', 'Waiting on'];
      heads.forEach((h, i) => {
        const right = i === 4;
        const hx = right ? xs[i] + cols[i] - 12 - measure(h.toUpperCase(), bold, 7.5) : xs[i];
        put(hx, y, h.toUpperCase(), 7.5, bold, QUIET);
      });
      y -= 5;
      page.drawLine({ start: { x: MARGIN, y }, end: { x: MARGIN + WIDTH, y }, thickness: 1.2, color: RULE });

      const rowH = 16;
      let drawn = 0;
      for (const r of rows) {
        if (y - rowH < MARGIN + FOOT + 14) break;
        y -= rowH;
        const base = y + 4;
        put(xs[0], base, fit(r.name, bold, 9, cols[0] - 9), 9, bold, INK);
        put(xs[1], base, fit(r.owner, regular, 9, cols[1] - 9), 9, regular, INK);
        // Health: square + word.
        square(xs[2], base, 8, RAG_COLOUR[r.rag]);
        put(xs[2] + 12, base, fit(r.statusWord, bold, 9, cols[2] - 14), 9, bold, RAG_INK[r.rag]);
        // Trend vs last month — arrow + word.
        trendCell(xs[3], base, r.trend);
        // Progress, right-aligned.
        const prog = r.progress == null ? '—' : `${r.progress}%`;
        const px = xs[4] + cols[4] - 12 - measure(prog, regular, 9);
        put(px, base, prog, 9, regular, r.progress == null ? QUIET : INK);
        // Go-live + slip.
        put(xs[5], base, fit(r.goLive, regular, 9, cols[5] - 30), 9, regular, r.goLive === 'Paused' ? QUIET : INK);
        if (r.slipDays > 0) {
          const gx = xs[5] + measure(fit(r.goLive, regular, 9, cols[5] - 30), regular, 9) + 4;
          put(gx, base, `+${r.slipDays}d`, 9, bold, r.slipRed ? RED : AMBER);
        }
        put(xs[6], base, fit(r.waitingOn || '—', regular, 9, cols[6] - 4), 9, regular, r.waitingOn ? INK : QUIET);
        page.drawLine({ start: { x: MARGIN, y }, end: { x: MARGIN + WIDTH, y }, thickness: 0.5, color: FAINT });
        drawn += 1;
      }
      const left = rows.length - drawn;
      if (left > 0) {
        y -= 14;
        put(MARGIN, y, `…and ${left} more, each with its own steering detail page overleaf.`, 9, regular, QUIET);
      }
    },

    /** MILESTONES — a project's own planned → forecast → actual table, with a slip. */
    milestones(rows: PackMilestone[]) {
      drawMilestones(rows, 'No committee milestones are set on this project yet.');
    },

    /** MILESTONES LANDING THIS CYCLE — the portfolio-level table (every open
     *  milestone across every project, soonest first), then the one-line read a
     *  steering meeting acts on: the go-live collision and the biggest drift. Capped
     *  so the page-1 summary stays readable; the tail lives on the detail pages. */
    cycleMilestones(rows: PackMilestone[], reading?: string) {
      const CAP = 9;
      drawMilestones(rows.slice(0, CAP), 'No committee milestones are set across the portfolio yet.');
      const over = rows.length - CAP;
      if (over > 0) {
        y -= 14;
        put(MARGIN, y, `…and ${over} more, each on its project's detail page overleaf.`, 9, regular, QUIET);
      }
      if (reading) {
        y -= 6;
        this.para(reading, { size: 9.5, colour: QUIET });
      }
    },

    /** WAITING ON — who, the ask (wrapped), and the age on the right. A ×N count
     *  after the who names how many cards carry the identical ask (the composer has
     *  already folded them into one line). */
    waiting(items: { who: string; ask: string; age: string; ageOld: boolean; count?: number }[]) {
      if (items.length === 0) {
        y -= 15;
        put(MARGIN, y, 'Nothing open — this project is waiting on no one.', 9.5, regular, QUIET);
        return;
      }
      const whoW = 118;
      const ageW = 52;
      const askW = WIDTH - whoW - ageW;
      for (const it of items) {
        const askLines = wrap(it.ask || '—', regular, 9.5, askW);
        const h = Math.max(1, askLines.length) * (9.5 * 1.4) + 5;
        room(h);
        const topBase = y - 9.5 * 1.4;
        const many = (it.count ?? 1) > 1;
        const countTxt = many ? `×${it.count}` : '';
        const countW = many ? measure(countTxt, regular, 8.5) + 4 : 0;
        const whoTxt = fit(it.who, bold, 9.5, whoW - 6 - countW);
        put(MARGIN, topBase, whoTxt, 9.5, bold, INK);
        if (many) put(MARGIN + measure(whoTxt, bold, 9.5) + 4, topBase, countTxt, 8.5, regular, QUIET);
        askLines.forEach((ln, n) => put(MARGIN + whoW, topBase - n * (9.5 * 1.4), ln, 9.5, regular, QUIET));
        if (it.age) {
          const ax = MARGIN + WIDTH - measure(it.age, bold, 9.5);
          put(ax, topBase, it.age, 9.5, bold, it.ageOld ? AMBER : QUIET);
        }
        y -= h;
        page.drawLine({ start: { x: MARGIN, y: y + 2 }, end: { x: MARGIN + WIDTH, y: y + 2 }, thickness: 0.5, color: FAINT });
      }
    },

    /** RISKS — the charter register: each risk in bold with its SEVERITY band on the
     *  right (Critical/High/Medium/Low from the register's one L×I scale — Q2), coloured
     *  on the band ramp (critical red, high orange, medium amber, low muted), and a muted
     *  mitigation + owner line beneath. Capped so one project's page is never
     *  swamped; the rest read as "+N more on the charter". Relocated from the
     *  charter, never re-judged. */
    risks(items: PackRisk[]) {
      if (items.length === 0) {
        y -= 15;
        put(MARGIN, y, 'No risks are recorded on this project’s charter.', 9.5, regular, QUIET);
        return;
      }
      for (const it of items.slice(0, RISK_CAP)) {
        const sevW = it.severity ? measure(it.severity, bold, 9) + 8 : 0;
        const riskLines = wrap(it.risk || '—', bold, 9.5, WIDTH - sevW);
        const detail = [it.mitigation ? `Mitigation: ${it.mitigation}` : '', it.owner ? `Owner: ${it.owner}` : '']
          .filter(Boolean)
          .join('    ');
        const detailLines = detail ? wrap(detail, regular, 9, WIDTH) : [];
        const h = Math.max(1, riskLines.length) * (9.5 * 1.4) + detailLines.length * (9 * 1.35) + 7;
        room(h);
        const topBase = y - 9.5 * 1.4;
        riskLines.forEach((ln, n) => put(MARGIN, topBase - n * (9.5 * 1.4), ln, 9.5, bold, INK));
        if (it.severity) {
          // The band ramp — critical red, high orange, medium amber, low muted — matching
          // the register and the status drill (the one L×I scale, Q2).
          const s = it.severity;
          const colour = /\bcritical\b/i.test(s)
            ? RED
            : /\bhigh\b/i.test(s)
              ? ORANGE
              : /\bmedium\b/i.test(s)
                ? AMBER
                : QUIET;
          put(MARGIN + WIDTH - measure(it.severity, bold, 9), topBase, it.severity, 9, bold, colour);
        }
        let dy = topBase - Math.max(1, riskLines.length) * (9.5 * 1.4) + 2;
        detailLines.forEach((ln) => {
          put(MARGIN, dy, ln, 9, regular, QUIET);
          dy -= 9 * 1.35;
        });
        y -= h;
        page.drawLine({ start: { x: MARGIN, y: y + 2 }, end: { x: MARGIN + WIDTH, y: y + 2 }, thickness: 0.5, color: FAINT });
      }
      if (items.length > RISK_CAP) {
        y -= 13;
        put(MARGIN, y, `+${items.length - RISK_CAP} more on the charter`, 9, regular, QUIET);
      }
    },

    /** DECISION NEEDED — a sunk inset (no coloured side-rail), a cap label, the ask. */
    decisionInset(text: string) {
      const inner = WIDTH - 24;
      const lines = wrap(text, regular, 10, inner);
      const height = 12 + 10 + lines.length * (10 * 1.5) + 10;
      room(height + 8);
      y -= height + 4;
      page.drawRectangle({ x: MARGIN, y, width: WIDTH, height, color: SUNK, borderColor: RULE, borderWidth: 0.75 });
      let ty = y + height - 12;
      put(MARGIN + 12, ty, 'DECISION NEEDED', 8, bold, QUIET);
      ty -= 12;
      lines.forEach((ln) => {
        ty -= 10 * 1.5 - 4;
        put(MARGIN + 12, ty, ln, 10, regular, INK);
      });
      y -= 2;
    },

    /** ONE CLOSED PROJECT — a sunk inset (no coloured rail): the project and an
     *  outcome pill, the after-action outcome, the schedule / cost / lessons read,
     *  and the single lesson to carry. Composed from the project's signed report. */
    closure(c: PackClosure) {
      const pad = 14;
      const inner = WIDTH - pad * 2;
      const outLines = wrap(c.outcome, regular, 10, inner);
      const cfLines = c.carryForward ? wrap(`Carry forward: ${c.carryForward}`, regular, 10, inner) : [];
      const headH = 20;
      const outH = outLines.length * (10 * 1.5);
      const vrowH = 28;
      const cfH = cfLines.length ? cfLines.length * (10 * 1.45) + 6 : 0;
      const height = pad + headH + outH + 6 + vrowH + cfH + pad - 4;
      room(height + 12);
      y -= height + 10;
      page.drawRectangle({ x: MARGIN, y, width: WIDTH, height, color: SUNK, borderColor: RULE, borderWidth: 0.75 });
      let ty = y + height - pad;

      // Header — the project name, and an outcome pill on the right (soft tint, no border).
      put(MARGIN + pad, ty - 10, c.project, 12.5, bold, INK);
      const pillText = c.pillWord.toUpperCase();
      const pillW = bold.widthOfTextAtSize(pillText, 8) + 16;
      const pillFill = c.pillOk ? rgb(0.894, 0.949, 0.918) : rgb(0.965, 0.933, 0.867);
      const pillInk = c.pillOk ? GREEN : AMBER;
      page.drawRectangle({ x: MARGIN + WIDTH - pad - pillW, y: ty - 13, width: pillW, height: 16, color: pillFill });
      put(MARGIN + WIDTH - pad - pillW + 8, ty - 9, pillText, 8, bold, pillInk);
      ty -= headH + 2;

      // Outcome.
      outLines.forEach((ln) => {
        ty -= 10 * 1.5 - 3;
        put(MARGIN + pad, ty, ln, 10, regular, INK);
      });
      ty -= 12;

      // The schedule / cost / lessons read — three stacked key/value columns.
      const cols: { k: string; v: string; tone: 'good' | 'bad' | 'plain' }[] = [
        { k: 'Schedule', v: c.schedule, tone: c.scheduleTone },
        ...(c.cost ? [{ k: 'Cost', v: c.cost, tone: c.costTone }] : []),
        { k: 'Lessons logged', v: String(c.lessonsLogged), tone: 'plain' as const },
      ];
      let vx = MARGIN + pad;
      for (const col of cols) {
        const vInk = col.tone === 'good' ? GREEN : col.tone === 'bad' ? RED : INK;
        put(vx, ty, col.k.toUpperCase(), 8, bold, QUIET);
        put(vx, ty - 13, col.v, 11.5, bold, vInk);
        const w = Math.max(bold.widthOfTextAtSize(col.v, 11.5), bold.widthOfTextAtSize(col.k.toUpperCase(), 8));
        vx += w + 30;
      }
      ty -= vrowH;

      // Carry forward — the label teal, the lesson in ink, wrapped.
      cfLines.forEach((ln, i) => {
        ty -= 10 * 1.45;
        if (i === 0) {
          const label = 'Carry forward: ';
          put(MARGIN + pad, ty, label, 10, bold, BRAND);
          put(MARGIN + pad + bold.widthOfTextAtSize(label, 10), ty, ln.replace(/^Carry forward:\s*/, ''), 10, regular, INK);
        } else {
          put(MARGIN + pad, ty, ln, 10, regular, INK);
        }
      });
      y -= 4;
    },

    /** DELIVERY EVENTS — what went live this cycle, one entry per board that crossed
     *  into service. A bold lead ("Project — went live 18 Sept") with the schedule
     *  variance right-aligned in its tone, then a quiet detail line: planned → actual,
     *  scope shipped vs deferred, and the budget at go-live. Modelled on the risks
     *  list, hairline-separated and page-break safe. */
    deliveryEvents(items: PackDeliveryEvent[]) {
      for (const it of items) {
        const chipW = it.schedule ? measure(it.schedule, bold, 9) + 8 : 0;
        const headLines = wrap(`${it.project} — went live ${it.wentLive}`, bold, 9.5, WIDTH - chipW);
        const bits: string[] = [
          it.planned ? `Planned ${it.planned} → actual ${it.wentLive}` : `Went live ${it.wentLive}`,
          it.scope,
        ];
        if (it.budget) bits.push(`budget ${it.budget} at Go-Live`);
        if (it.provisional) bits.push('provisional — a delivery phase was still open');
        const detailLines = wrap(bits.join('  ·  '), regular, 9, WIDTH);
        const h = Math.max(1, headLines.length) * (9.5 * 1.4) + detailLines.length * (9 * 1.35) + 7;
        room(h);
        const topBase = y - 9.5 * 1.4;
        headLines.forEach((ln, n) => put(MARGIN, topBase - n * (9.5 * 1.4), ln, 9.5, bold, INK));
        if (it.schedule) {
          const tone = it.scheduleTone === 'good' ? GREEN : it.scheduleTone === 'bad' ? RED : QUIET;
          put(MARGIN + WIDTH - measure(it.schedule, bold, 9), topBase, it.schedule, 9, bold, tone);
        }
        let dy = topBase - Math.max(1, headLines.length) * (9.5 * 1.4) + 2;
        detailLines.forEach((ln) => {
          put(MARGIN, dy, ln, 9, regular, QUIET);
          dy -= 9 * 1.35;
        });
        y -= h;
        page.drawLine({ start: { x: MARGIN, y: y + 2 }, end: { x: MARGIN + WIDTH, y: y + 2 }, thickness: 0.5, color: FAINT });
      }
    },

    /** ONE FORWARD LESSON — a number, the headline with its kind tag, the
     *  recommendation to carry, and the project it came from. A hairline separates
     *  one from the next, so the roll-up reads as a list. */
    lessonForward(n: number, l: PackLessonForward, first: boolean) {
      const indent = 20;
      const inner = WIDTH - indent;
      const tag = l.kindLabel;
      const tagW = bold.widthOfTextAtSize(tag.toUpperCase(), 7.5) + 12;
      const titleLines = wrap(l.title, bold, 11, inner - tagW - 8);
      const recLines = wrap(`Carry forward: ${l.recommendation}`, regular, 10, inner);
      const height = titleLines.length * (11 * 1.3) + recLines.length * (10 * 1.45) + 18 + 5;
      room(height + 8);
      // A gap above the lesson; the separator sits in it, clear of the title and its
      // kind tag below (the tag box is taller than the text, so it needs the room).
      y -= 9;
      if (!first) {
        page.drawLine({ start: { x: MARGIN, y }, end: { x: MARGIN + WIDTH, y }, thickness: 0.75, color: RULE });
      }
      y -= 13;
      const top = y;
      put(MARGIN, top, String(n), 11, bold, BRAND);
      // Title lines; the kind tag sits after the first line's text.
      titleLines.forEach((ln, i) => {
        if (i > 0) y -= 11 * 1.3;
        put(MARGIN + indent, y, ln, 11, bold, INK);
        if (i === 0) {
          const tx = MARGIN + indent + bold.widthOfTextAtSize(ln, 11) + 8;
          page.drawRectangle({ x: tx, y: y - 2.5, width: tagW, height: 13, borderColor: RULE, borderWidth: 0.75 });
          put(tx + 6, y, tag.toUpperCase(), 7.5, bold, QUIET);
        }
      });
      recLines.forEach((ln, i) => {
        y -= 10 * 1.45;
        if (i === 0) {
          const label = 'Carry forward: ';
          put(MARGIN + indent, y, label, 10, bold, BRAND);
          put(MARGIN + indent + bold.widthOfTextAtSize(label, 10), y, ln.replace(/^Carry forward:\s*/, ''), 10, regular, INK);
        } else {
          put(MARGIN + indent, y, ln, 10, regular, INK);
        }
      });
      y -= 13;
      put(MARGIN + indent, y, l.source, 9, regular, QUIET);
      y -= 4;
    },
  };

  return api;
}

/* A run of text that is part bold, part regular — for a decision's "Project — ask". */
interface Seg {
  text: string;
  bold: boolean;
}
/** Wrap a "<bold> — <rest>" string, keeping the bold prefix bold across the wrap. */
function wrapMixed(
  full: string,
  boldPart: string,
  bold: PDFFont,
  regular: PDFFont,
  size: number,
  measure: number,
): Seg[][] {
  const clean = winAnsi(full);
  const boldClean = winAnsi(boldPart);
  const words: Seg[] = [];
  // Split into words, marking which fall inside the bold prefix.
  const boldEnd = boldClean.length;
  let at = 0;
  for (const word of clean.split(/(\s+)/)) {
    if (!word) continue;
    const isSpace = /^\s+$/.test(word);
    if (!isSpace) words.push({ text: word, bold: at < boldEnd });
    at += word.length;
  }
  const lines: Seg[][] = [];
  let line: Seg[] = [];
  let width = 0;
  for (const w of words) {
    const font = w.bold ? bold : regular;
    const ww = font.widthOfTextAtSize(w.text, size);
    const space = line.length ? regular.widthOfTextAtSize(' ', size) : 0;
    if (width + space + ww > measure && line.length) {
      lines.push(line);
      line = [];
      width = 0;
    }
    if (line.length) {
      line.push({ text: ` ${w.text}`, bold: w.bold });
      width += space + ww;
    } else {
      line.push(w);
      width += ww;
    }
  }
  if (line.length) lines.push(line);
  return lines;
}

/**
 * The steering pack, as bytes. Takes the composed pack and the date and lays it
 * out; the caller has already worked out what it says.
 */
export async function portfolioPackPdf(model: PackModel, facts: PackFacts): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  pdf.setTitle(`Monthly Steering Pack — ${model.workspaceName}`);
  pdf.setSubject(`Portfolio steering review — ${model.period}`);
  pdf.setProducer('FlightDeck');
  stampDates(pdf, asDate(facts.preparedOn));

  const art = await embed(pdf, facts.logo);
  const titleFont = await embedTitleFont(pdf);
  const pen = makePen(
    pdf,
    regular,
    bold,
    {
      workspaceName: facts.workspaceName || model.workspaceName,
      producedOn: asLongDate(facts.preparedOn),
      producedBy: facts.preparedBy,
      logo: art,
    },
    titleFont,
  );

  /* ── PAGE 1 — EXECUTIVE SUMMARY ─────────────────────────────────────────── */
  pen.eyebrow('Portfolio status');
  pen.title('Monthly Steering Pack');
  pen.sub(
    `Steering committee review · ${model.period} · ${model.projectCount} ${model.projectCount === 1 ? 'project' : 'projects'}`,
  );
  pen.titleRule();
  pen.para(model.verdict, { size: 10.5, gap: 2 });

  pen.label('Portfolio health');
  pen.distribution(model.health);

  pen.label('Milestones landing this cycle');
  pen.cycleMilestones(model.cycleMilestones, model.milestoneReading);

  pen.label('Decisions for this committee');
  if (model.decisions.length === 0) {
    pen.para('No decision is being asked of this committee this month.', { size: 9.5, colour: rgb(0.42, 0.46, 0.5) });
  } else {
    // Each distinct decision once; the card ref(s) let the committee find "which".
    model.decisions.forEach((d, i) =>
      pen.decision(
        i + 1,
        d.project,
        d.ask,
        d.refs.length ? `${d.consequence} ${d.refs.length === 1 ? 'Card' : 'Cards'}: ${d.refs.join(', ')}.` : d.consequence,
      ),
    );
  }

  pen.label('Budget position');
  pen.burn(model.budget);
  if (model.costedCount > 0 && model.budgetedCount < model.costedCount) {
    pen.para(
      `${model.budgetedCount} of ${model.costedCount} active ${model.costedCount === 1 ? 'project carries' : 'projects carry'} a budget; the rest are not yet costed.`,
      { size: 9, colour: rgb(0.42, 0.46, 0.5) },
    );
  }

  // Keep the glance whole: reserve its label, header and at least two rows so it is
  // never left as a header with every row pushed to the next page (which read as an
  // empty "…and N more"). Triggers a fresh page only when page 1 is genuinely too
  // full; a page with room for the table keeps it in place.
  pen.room(30 + 17 + 16 * Math.min(model.glance.length, 2));
  pen.label('Portfolio at a glance');
  pen.glance(model.glance);
  if (model.notTrackedCount > 0) {
    pen.para(
      `${model.notTrackedCount} ${model.notTrackedCount === 1 ? 'board is' : 'boards are'} not run as ${model.notTrackedCount === 1 ? 'a project' : 'projects'} (no plan, phases or budget) and ${model.notTrackedCount === 1 ? 'is' : 'are'} not in this review.`,
      { size: 9, colour: rgb(0.42, 0.46, 0.5) },
    );
  }

  /* ── LIFECYCLE ROLL-UP (#435 + #491) — the cycle's milestone moments on one page:
        what went live, what closed, and the lessons to carry. Only when there is
        something to show, so a pack from a quiet month carries no empty page. Sits
        after the decisions, before the per-project detail. ──────────────────────── */
  if (model.deliveryEvents.length > 0 || model.closed.length > 0 || model.lessonsForward.length > 0) {
    pen.newPage();
    const wentLive = model.deliveryEvents.length;
    pen.eyebrow(wentLive > 0 ? 'Delivery & closure' : 'Learning & closure');
    pen.title(
      wentLive > 0
        ? model.closed.length > 0
          ? 'Delivered & closed this cycle'
          : 'Went live this cycle'
        : 'Closed this cycle & what we learned',
    );
    const bits: string[] = [];
    if (wentLive > 0) bits.push(`${wentLive} ${wentLive === 1 ? 'project' : 'projects'} went live`);
    if (model.closed.length > 0) bits.push(`${model.closed.length} ${model.closed.length === 1 ? 'project' : 'projects'} closed`);
    if (model.lessonsForwardTotal > 0) {
      bits.push(`${model.lessonsForwardTotal} ${model.lessonsForwardTotal === 1 ? 'lesson' : 'lessons'} carried forward`);
    }
    pen.sub(`Steering committee review · ${model.period} · ${bits.join(' · ')}`);
    pen.titleRule();

    if (model.deliveryEvents.length > 0) {
      pen.label('Went live this cycle');
      pen.para(
        'Projects that crossed from delivery into service this cycle — the as-delivered record frozen at Go-Live: planned against actual, what shipped, and the budget it landed on. The full record is kept in each board’s History.',
        { size: 9.5, colour: QUIET, gap: 4 },
      );
      pen.deliveryEvents(model.deliveryEvents);
    }

    if (model.closed.length > 0) {
      pen.label('Closed since the last review');
      pen.para(
        'Projects that reached close this cycle — the after-action outcome in one line, the variance behind it, and the one lesson to carry. The full after-action report is filed against each project.',
        { size: 9.5, colour: QUIET, gap: 4 },
      );
      for (const c of model.closed) pen.closure(c);
    }

    if (model.lessonsForward.length > 0) {
      pen.label('Lessons being carried forward');
      pen.para(
        'The recommendation-bearing lessons raised across the portfolio this cycle — what the next project should do differently. Newest first.',
        { size: 9.5, colour: QUIET, gap: 6 },
      );
      model.lessonsForward.forEach((l, i) => pen.lessonForward(i + 1, l, i === 0));
      const more = model.lessonsForwardTotal - model.lessonsForward.length;
      pen.para(
        more > 0
          ? `…and ${more} more ${more === 1 ? 'lesson' : 'lessons'} carried forward. All lessons remain in the workspace Lessons library.`
          : 'All lessons remain in the workspace Lessons library.',
        { size: 9, colour: QUIET },
      );
    }
  }

  /* ── PAGE 2+ — ONE PROJECT PER PAGE ─────────────────────────────────────── */
  for (const p of model.projects) {
    pen.newPage();
    pen.eyebrow('Steering detail');
    pen.projectHeader(p.name, p.statusWord);
    pen.para(p.verdict, { size: 10.5 });
    pen.signals([p.schedule, p.budget, p.blockers]);
    pen.label('Milestones');
    pen.milestones(p.milestones);
    pen.label('Budget');
    pen.burn(p.budgetFigures);
    pen.label('Waiting on');
    pen.waiting(p.waiting);
    pen.label('Risks');
    pen.risks(p.risks);
    if (p.decision) pen.decisionInset(p.decision);
    pen.label('Moved this month');
    pen.para(p.moved, { size: 10 });
  }

  stampFooters(
    pdf,
    `${model.workspaceName}  ·  Portfolio  ·  Steering review · ${model.period}`,
    regular,
  );
  return pdf.save();
}
